import { useSuspenseQuery } from '@apollo/client'
import { UntitledIcon } from '@faceup/icons'
import { useLocalization } from '@faceup/localization'
import { type Report, ReportStatusTag, useSuspenseDecrypt } from '@faceup/report'
import { Card, ucCaretRight } from '@faceup/ui'
import { Collapse, Typography } from '@faceup/ui-base'
import {
  FormItemType,
  Institution,
  type Language,
  getTranslation as utilsGetTranslation,
} from '@faceup/utils'
import moment from 'moment-timezone'
import { type ReactNode, Suspense } from 'react'
import { graphql } from '../../__generated__'
import { reportStatusMessages } from '../../Shared/translations'
import { FormattedMessage, useIntl } from '../../TypedIntl'
import { messages } from './messages'
import { SentInformationContent } from './SentInformationContent'
import { SummaryField } from './SummaryField'

const query = graphql(`
  # sourceLanguage/targetLanguage not used directly but needed by transitive
  # useFormItemsAnswers_formItemAnswer fragment selections.
  query ReportSummaryQuery($sourceLanguage: Language, $targetLanguage: Language) {
    companyVictimViewer {
      id
      case {
        id
        tag
        createdAt
        lastChangeDate
        status {
          id
          groupName
          color
        }
        organizationalUnit {
          id
          type
        }
        channel {
          id
          isDefault
          defaultLanguage
          config {
            ... on FormReportSourceConfiguration {
              id
              identificationFormItems {
                id
                type
                labelTranslations {
                  language
                  translation
                }
              }
            }
          }
        }
        ...SentInformationContent_case
        ... on CompanyReport {
          ...UseReportBody_reportNoVariables
          ...SentInformationContent_companyReport
        }
      }
    }
  }
`)

export const ReportSummary = () => (
  <Suspense fallback={<Card loading className='w-full p-8' />}>
    <ReportSummaryContent />
  </Suspense>
)

const ReportSummaryContent = () => {
  const { formatMessage } = useIntl()
  const { language } = useLocalization()

  const { data } = useSuspenseQuery(query, { fetchPolicy: 'network-only' })

  const aCase = data.companyVictimViewer?.case

  if (!aCase) {
    return <Card loading className='w-full p-8' />
  }

  const report = aCase.__typename === 'CompanyReport' ? aCase : null
  const currentStatus = aCase.status
  const channel = aCase.channel

  const isSchoolDefaultForm =
    aCase.organizationalUnit?.type === Institution.School && channel.isDefault

  const config =
    channel.config?.__typename === 'FormReportSourceConfiguration' ? channel.config : null
  const formItemSenderName = config?.identificationFormItems.find(
    formItem => formItem.type === FormItemType.SenderName
  )

  const getTranslation = (
    translations: { language: Language; translation: string }[] | undefined
  ) => utilsGetTranslation(translations ?? [], language, channel.defaultLanguage)

  const senderNameLabel = isSchoolDefaultForm ? (
    <FormattedMessage {...messages.victimName} />
  ) : (
    getTranslation(formItemSenderName?.labelTranslations) || (
      <FormattedMessage {...messages.senderIdentity} />
    )
  )

  return (
    <Card className='p-8 w-full flex flex-col gap-8' data-test='report-summary'>
      <Typography.Title level={4} className='!m-0'>
        <FormattedMessage {...messages.title} />
      </Typography.Title>

      <div className='bg-colorFillTertiary rounded-xl pt-4 pb-6 px-6'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
          <SummaryField
            label={<FormattedMessage {...messages.reportId} />}
            value={`#${aCase.tag}`}
            dataTest='report-summary-report-id'
          />
          <SummaryField
            label={<FormattedMessage {...messages.status} />}
            value={
              <ReportStatusTag
                name={formatMessage(reportStatusMessages[currentStatus.groupName])}
                color={currentStatus.color}
              />
            }
          />
          <SummaryField
            label={<FormattedMessage {...messages.reported} />}
            value={moment(aCase.createdAt).format('LLL')}
          />
          <SummaryField
            label={<FormattedMessage {...messages.lastAction} />}
            value={moment(aCase.lastChangeDate).format('LLL')}
          />
          {report && (
            <Suspense fallback={null}>
              <DecryptedFields report={report} senderNameLabel={senderNameLabel} />
            </Suspense>
          )}
        </div>
      </div>

      <Collapse
        bordered
        expandIcon={({ isActive }) => (
          <UntitledIcon icon={ucCaretRight} rotate={isActive ? 90 : 0} />
        )}
        items={[
          {
            key: 'sentInformation',
            label: (
              <Typography.Text strong data-test='report-summary-sent-information-toggle'>
                <FormattedMessage {...messages.sentInformation} />
              </Typography.Text>
            ),
            children: <SentInformationContent aCase={aCase} report={report} />,
          },
        ]}
      />
    </Card>
  )
}

type DecryptedFieldsProps = {
  report: NonNullable<Report['reportNoVar']>
  senderNameLabel: ReactNode
}

const DecryptedFields = ({ report, senderNameLabel }: DecryptedFieldsProps) => {
  const { senderName } = useSuspenseDecrypt({ reportNoVar: report })

  return (
    <SummaryField
      label={senderNameLabel}
      value={senderName || <FormattedMessage {...messages.anonymous} />}
    />
  )
}
